import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Sparkles, ArrowLeft, Link2, Zap, RefreshCw } from 'lucide-react';
import { useMyContext } from '../../../../context/MyContext';
import Sidebar from '../../../../component/b2c/sidebar/sidebar';
import NavBar from '../../../../component/b2c/navBar/navBar';

// 👇 adjust these import paths as per your actual folder structure
import ShopifyConnect from '../Sopify/SDashboard';
import WooCommerceConnect from '../wooCom/wooCom';
import ZohoDashboard from '../Zoho/ZohoDashboard.jsx';

// ⬇️ small inline illustration shown in the header row (between Back button and title)
const IntegrationIllustration = ({ isDarkMode, accentFrom, accentTo }) => {
  return (
    <div className="flex justify-center select-none pointer-events-none">
      <svg width="140" height="48" viewBox="0 0 140 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="lineGradHeader" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={accentFrom} stopOpacity="0.9" />
            <stop offset="100%" stopColor={accentTo} stopOpacity="0.9" />
          </linearGradient>
        </defs>

        {/* connecting dashed path */}
        <path
          d="M25 24 C 50 6, 90 42, 115 24"
          stroke="url(#lineGradHeader)"
          strokeWidth="2"
          strokeDasharray="4 5"
          fill="none"
        />

        {/* Pickupxpress node */}
        <circle cx="25" cy="24" r="10" fill={isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.6)'} stroke={accentFrom} strokeWidth="1.5" />
        <circle cx="25" cy="24" r="3.5" fill={accentFrom} />

        {/* mid pulse node */}
        <circle cx="70" cy="16" r="6" fill={isDarkMode ? 'rgba(168,85,247,0.15)' : 'rgba(168,85,247,0.12)'} stroke={accentTo} strokeWidth="1.2" />

        {/* target app node */}
        <circle cx="115" cy="24" r="10" fill={isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.6)'} stroke={accentTo} strokeWidth="1.5" />
        <circle cx="115" cy="24" r="3.5" fill={accentTo} />

        {/* small orbiting dots for a "syncing" feel */}
        <circle cx="45" cy="35" r="2" fill={accentFrom} opacity="0.6" />
        <circle cx="95" cy="38" r="2" fill={accentTo} opacity="0.6" />
      </svg>
    </div>
  );
};

// ⬇️ big hub-and-spoke illustration for the empty space on the main integrations page
const HubIllustration = ({ isDarkMode }) => {
  const nodeFill = isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.55)';
  return (
    <div className="w-full flex justify-center select-none pointer-events-none mt-10">
      <svg width="100%" height="360" viewBox="0 0 680 360" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="hubLineShopify" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#a855f7" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#22c55e" stopOpacity="0.8" />
          </linearGradient>
          <linearGradient id="hubLineWoo" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#a855f7" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#7c3aed" stopOpacity="0.8" />
          </linearGradient>
          <linearGradient id="hubLineZoho" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#a855f7" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#f97316" stopOpacity="0.8" />
          </linearGradient>
        </defs>

        {/* orbit rings around the hub */}
        <circle cx="340" cy="170" r="90" stroke={isDarkMode ? 'rgba(168,85,247,0.15)' : 'rgba(168,85,247,0.18)'} strokeWidth="1" fill="none" />
        <circle cx="340" cy="170" r="140" stroke={isDarkMode ? 'rgba(168,85,247,0.08)' : 'rgba(168,85,247,0.1)'} strokeWidth="1" fill="none" />

        {/* connecting dashed lines from hub to each satellite */}
        <path d="M340 170 C 260 110, 180 90, 130 90" stroke="url(#hubLineShopify)" strokeWidth="2" strokeDasharray="5 6" fill="none" />
        <path d="M340 170 C 300 230, 260 280, 220 300" stroke="url(#hubLineWoo)" strokeWidth="2" strokeDasharray="5 6" fill="none" />
        <path d="M340 170 C 430 130, 500 100, 555 90" stroke="url(#hubLineZoho)" strokeWidth="2" strokeDasharray="5 6" fill="none" />

        {/* central Pickupxpress hub node */}
        <circle cx="340" cy="170" r="34" fill={nodeFill} stroke="#a855f7" strokeWidth="1.5" />
        <circle cx="340" cy="170" r="9" fill="#a855f7" />
        <text x="340" y="222" textAnchor="middle" className="t" fill={isDarkMode ? '#e9d5ff' : '#7e22ce'} fontSize="13" fontWeight="600">Pickupxpress</text>

        {/* Shopify satellite */}
        <circle cx="130" cy="90" r="22" fill={nodeFill} stroke="#22c55e" strokeWidth="1.5" />
        <circle cx="130" cy="90" r="6" fill="#22c55e" />
        <text x="130" y="130" textAnchor="middle" fill={isDarkMode ? '#bbf7d0' : '#15803d'} fontSize="12">Shopify</text>

        {/* WooCommerce satellite */}
        <circle cx="220" cy="300" r="22" fill={nodeFill} stroke="#a855f7" strokeWidth="1.5" />
        <circle cx="220" cy="300" r="6" fill="#a855f7" />
        <text x="220" y="332" textAnchor="middle" fill={isDarkMode ? '#e9d5ff' : '#7e22ce'} fontSize="12">WooCommerce</text>

        {/* Zoho satellite */}
        <circle cx="555" cy="90" r="22" fill={nodeFill} stroke="#f97316" strokeWidth="1.5" />
        <circle cx="555" cy="90" r="6" fill="#f97316" />
        <text x="555" y="130" textAnchor="middle" fill={isDarkMode ? '#fed7aa' : '#c2410c'} fontSize="12">Zoho</text>

        {/* small floating sync dots for ambient motion feel */}
        <circle cx="230" cy="140" r="3" fill="#a855f7" opacity="0.5" />
        <circle cx="440" cy="150" r="3" fill="#a855f7" opacity="0.5" />
        <circle cx="270" cy="230" r="3" fill="#a855f7" opacity="0.4" />
        <circle cx="470" cy="220" r="3" fill="#a855f7" opacity="0.4" />
        <circle cx="600" cy="180" r="4" fill="#f97316" opacity="0.4" />
        <circle cx="80" cy="180" r="4" fill="#22c55e" opacity="0.4" />
      </svg>
    </div>
  );
};

const Integration = () => {
  const { isToggleSidebar, isDarkMode } = useMyContext();

  // ⬇️ tab state instead of navigation
  const [activeTab, setActiveTab] = useState(null);

  const handleShopifyClick = () => {
    setActiveTab(activeTab === 'shopify' ? null : 'shopify');
  };

  const handleWoocommerceClick = () => {
    setActiveTab(activeTab === 'woocommerce' ? null : 'woocommerce');
  };

  const handleZohoClick = () => {
    setActiveTab(activeTab === 'zoho' ? null : 'zoho');
  };

  const handleBackClick = () => {
    setActiveTab(null);
  };

  const cards = [
    {
      key: 'shopify',
      badgeBg: 'bg-gradient-to-br from-green-400 to-green-600',
      badgeLetter: 'S',
      title: 'Shopify',
      desc: 'Auto-fetch your Shopify orders directly into Pickupxpress and manage fulfillment without switching platforms.',
      onClick: handleShopifyClick,
      accentFrom: '#22c55e',
      accentTo: '#a855f7',
    },
    {
      key: 'woocommerce',
      badgeBg: 'bg-gradient-to-br from-purple-500 to-purple-700',
      badgeLetter: 'W',
      title: 'WooCommerce',
      desc: 'Connect your WooCommerce store to sync orders, track shipments, and automate pickup requests seamlessly.',
      onClick: handleWoocommerceClick,
      accentFrom: '#a855f7',
      accentTo: '#6366f1',
    },
    {
      key: 'zoho',
      badgeBg: 'bg-gradient-to-br from-red-500 to-orange-500',
      badgeLetter: 'Z',
      title: 'Zoho',
      desc: 'Integrate Zoho to auto-fetch orders and keep your shipment data in sync across both platforms in real time.',
      onClick: handleZohoClick,
      accentFrom: '#f97316',
      accentTo: '#ef4444',
    },
  ];

  // ⬇️ maps tab key -> component to render below
  const renderActiveTabContent = () => {
    switch (activeTab) {
      case 'shopify':
        return <ShopifyConnect />;
      case 'woocommerce':
        return <WooCommerceConnect />;
      case 'zoho':
        return <ZohoDashboard />;
      default:
        return null;
    }
  };

  // ⬇️ current active card's meta (for illustration colors + title in back header)
  const activeCard = cards.find((c) => c.key === activeTab);

  return (
    <div className={`h-screen flex flex-col overflow-hidden ${isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-white'}`}>
      <div className="z-10">
        <NavBar />
      </div>
      <div className="flex flex-1 overflow-hidden">
        {isToggleSidebar && (
          <div
            className={`fixed z-20 h-full w-64 md:relative duration-300 bg-gray-200 dark:bg-darkmodeColor dark:text-white md:bg-transparent mt-4 md:w-64 md:z-20`}
            style={
              window.innerWidth < 768
                ? { top: "113px", height: "calc(100vh - 115px)", overflowY: 'auto' }
                : {}
            }>
            <Sidebar />
          </div>
        )}

        <div
          className={`flex-1 p-3 overflow-y-auto  relative ${isDarkMode ? 'bg-darkmodeColor' : 'bg-gray-200'}`}
        >
          {/* Ambient background glow */}
          {/* <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="absolute -top-24 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
            <div className="absolute top-1/3 right-0 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-80 h-80 bg-fuchsia-500/10 rounded-full blur-3xl" />
          </div> */}

          <div className="max-w-6xl mx-auto relative">
            {/* ⬇️ Cards grid only shows when no tab is active */}
            {!activeTab && (
              <>
                <motion.div
                  initial={{ opacity: 0, y: -16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="text-center"
                >
                  <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4 border ${isDarkMode ? 'bg-white/5 border-white/10 text-purple-300' : 'bg-purple-50 border-purple-200 text-purple-600'} text-xs font-semibold tracking-wide uppercase`}>
                    <Sparkles className="w-3.5 h-3.5" />
                    Integrations Hub
                  </div>
                  {/* <h1 className={`text-4xl md:text-5xl font-extrabold tracking-tight  bg-clip-text text-transparent bg-gradient-to-r ${isDarkMode ? 'from-white via-purple-200 to-indigo-200' : 'from-gray-900 via-purple-700 to-indigo-700'}`}>
                    Channel Integration
                  </h1> */}
                  {/* <p className={`text-sm md:text-base max-w-xl mx-auto ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Pre-integrated with Shopify, WooCommerce and Zoho — your orders get auto-fetched by Pickupxpress.
                  </p> */}
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {cards.map((card, idx) => {
                    const isActive = activeTab === card.key;
                    return (
                      <motion.div
                        key={card.key}
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: idx * 0.1 }}
                        whileHover={{ y: -8 }}
                        onClick={card.onClick}
                        role="button"
                        tabIndex={0}
                        className={`group relative overflow-hidden rounded-2xl p-2 flex flex-col justify-between transition-all duration-300 cursor-pointer
                          ${isDarkMode
                            ? 'bg-white/[0.07] border border-white/15 shadow-[0_8px_32px_0_rgba(0,0,0,0.37)] hover:shadow-[0_8px_40px_0_rgba(168,85,247,0.25)] hover:border-purple-400/30'
                            : 'bg-white/30 border border-white/60 shadow-[0_8px_32px_0_rgba(31,38,135,0.15)] hover:shadow-[0_8px_40px_0_rgba(147,51,234,0.2)] hover:border-purple-300/60'
                          } backdrop-blur-xl backdrop-saturate-150
                          ${isActive ? (isDarkMode ? 'ring-2 ring-purple-400/60 border-purple-400/50' : 'ring-2 ring-purple-500/50 border-purple-400') : ''}
                        `}
                        style={{ WebkitBackdropFilter: 'blur(20px) saturate(150%)' }}
                      >
                        {/* Glass top highlight sheen */}
                        <div className={`pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent ${isDarkMode ? 'via-white/40' : 'via-white/90'} to-transparent`} />
                        {/* Soft inner glow blob */}
                        <div className="pointer-events-none absolute -top-10 -right-10 w-32 h-32 rounded-full bg-purple-400/20 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        {/* Diagonal glass reflection */}
                        <div className="pointer-events-none absolute -inset-x-6 -top-1/2 h-[200%] w-1/3 bg-gradient-to-b from-white/20 via-white/5 to-transparent rotate-12 opacity-0 group-hover:opacity-60 transition-opacity duration-500" />

                        <div className="relative">
                          <div className="flex items-center justify-between ">
                            <div className="flex items-center gap-3">
                              <div className={`w-9 h-9 rounded-lg flex items-center justify-center shadow-sm ring-1 ring-white/30 ${card.badgeBg}`}>
                                <span className="text-white text-sm font-bold">{card.badgeLetter}</span>
                              </div>
                              <h3 className={`text-base font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {card.title}
                              </h3>
                            </div>
                            <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0 drop-shadow-sm" />
                          </div>

                          {/* <p className={`text-sm leading-tight mb-6 min-h-[60px] ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {card.desc}
                          </p> */}
                        </div>

                        {/* <div className="relative flex justify-end">
                          <button
                            onClick={card.onClick}
                            className={`text-sm font-semibold py-2 px-4 rounded-lg border backdrop-blur-md transition-all duration-300 ${isDarkMode ? 'bg-white/10 hover:bg-purple-400/30 text-white border-white/20 hover:border-purple-300/50' : 'bg-white/50 hover:bg-purple-200/70 text-gray-800 border-white/70 hover:border-purple-400'}`}
                          >
                            View Dashboard
                          </button>
                        </div> */}
                      </motion.div>
                    );
                  })}
                </div>

                {/* ⬇️ Big illustration filling the empty space below the cards */}
                <HubIllustration isDarkMode={isDarkMode} />
              </>
            )}

            {/* ⬇️ Active tab's content (dashboard or connect form) renders here, cards hidden */}
            <AnimatePresence mode="wait">
              {activeTab && (
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.3 }}
                  className="mt-1"
                >
                  {/* Back button + illustration + active tab title, all in one tight row */}
                  <div className="flex items-center justify-between ">
                    <button
                      onClick={handleBackClick}
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-300 border backdrop-blur-md shrink-0
                        ${isDarkMode
                          ? 'bg-white/[0.07] border-white/15 text-white hover:bg-purple-400/20 hover:border-purple-400/40'
                          : 'bg-white/50 border-white/70 text-gray-800 hover:bg-purple-100/70 hover:border-purple-300'
                        }`}
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Back
                    </button>

                    {activeCard && (
                      <IntegrationIllustration
                        isDarkMode={isDarkMode}
                        accentFrom={activeCard.accentFrom}
                        accentTo={activeCard.accentTo}
                      />
                    )}

                    <div className="flex items-center gap-2 shrink-0">
                      {activeCard && (
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shadow-sm ring-1 ring-white/30 ${activeCard.badgeBg}`}>
                          <span className="text-white text-sm font-bold">{activeCard.badgeLetter}</span>
                        </div>
                      )}
                      <h3 className={`text-base font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {activeCard?.title}
                      </h3>
                    </div>
                  </div>

                  {/* Content starts right below the header row now */}
                  {renderActiveTabContent()}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Integration;